/**
 * lwcCrashGuard — contención quirúrgica del bug upstream de lightweight-charts
 * (#2044, abierto sin fix en 4.2.3–5.2.0).
 *
 * El bug: cuando un setData elimina time points de la escala (compactación),
 * el resto de series conserva UN FRAME con el mapeo de índices obsoleto; si el
 * crosshair está aplicado en ese frame, LWC revienta en ensureNotNull con
 * "Value is null" (CrosshairMarksPaneView y labels del crosshair). Al frame
 * siguiente las series se revalidan solas (análisis del maintainer).
 *
 * QUIRÚRGICO a propósito (condición del usuario): solo captura errores cuyo
 * mensaje es EXACTAMENTE 'Value is null' — es la firma de ensureNotNull de
 * LWC, ningún código propio lanza ese mensaje. Cualquier otro error pasa
 * intacto (sin preventDefault): enmascarar bugs propios es inaceptable.
 *
 * Métrica: cada captura se cuenta (getCrashCount / window.__lwcCrashGuard en
 * modo debug) y se loguea gateada por el logger (silenciosa en prod salvo
 * ?debug=1), con el STACK de las primeras capturas de cada ráfaga para saber
 * si los throws vienen de los puntos ya parcheados (P1–P4) o de ensureNotNull
 * nuevos. La re-invalidación del chart está moderada (un RAF y una ventana
 * mínima): la re-invalidación inmediata realimentaba el crash en tormenta
 * (465 capturas tras un solo reset, 2026-08-06). Si la frecuencia supera lo
 * anecdótico, se reevalúa el parche de LWC en build (docs/BACKLOG.md).
 * Retirar esta guarda cuando TradingView corrija #2044.
 */

import { warn as debugWarn } from '@/utils/logger'

const LWC_ERROR_MESSAGE = 'Value is null'

// ── Re-invalidación frenada (bug de la tormenta, 2026-08-06) ────────────────
// La re-invalidación inmediata por captura (applyOptions({})) realimentaba el
// crash: mientras la escala sigue transitoria, cada applyOptions fuerza un
// update completo que vuelve a lanzar "Value is null" → guardia → applyOptions
// → … (465 ocurrencias registradas tras UN clic de reset). La captura sigue
// siendo inmediata; solo la REACCIÓN se modera: un solo RAF en vuelo (flag de
// pendiente) y una ventana mínima entre revalidaciones.
const MIN_REVALIDATION_MS = 250

// ── Diagnóstico de ráfagas: stacks de las primeras N capturas de cada ráfaga
// (con ?debug=1 / quantum_debug). Sin el stack estamos ciegos: no se distingue
// un throw de los puntos ya parcheados (P1–P4) de un ensureNotNull NUEVO en el
// mismo estado transitorio.
const STACKS_PER_BURST = 5
const BURST_GAP_MS = 2000
const MAX_STACKS_KEPT = 50

let crashCount = 0
let revalidationCount = 0
let revalidationPending = false
let lastRevalidationAt = 0
let lastCaptureAt = 0
let burstIndex = 0
let burstCaptureCount = 0
let capturedStacks = []
let installed = false
let handlerRef = null

/** Firma exacta del bug upstream: ensureNotNull lanza un Error plano con
 * mensaje 'Value is null'. Se exige constructor Error exacto (ni TypeError ni
 * subclases): cuanto más estrecha la firma, menor riesgo de tragar bugs propios. */
export function isLwcCompactionCrash(event) {
  const err = event?.error
  return err instanceof Error && err.constructor === Error && err.message === LWC_ERROR_MESSAGE
}

export function getCrashCount() {
  return crashCount
}

export function getRevalidationCount() {
  return revalidationCount
}

/** Copia de los stacks capturados (diagnóstico: origen real de la ráfaga). */
export function getCapturedStacks() {
  return capturedStacks.slice()
}

/** Solo test: reinicia el contador y desinstala el handler. */
export function __resetForTests() {
  if (handlerRef && typeof window !== 'undefined') {
    window.removeEventListener('error', handlerRef, true)
  }
  handlerRef = null
  crashCount = 0
  revalidationCount = 0
  revalidationPending = false
  lastRevalidationAt = 0
  lastCaptureAt = 0
  burstIndex = 0
  burstCaptureCount = 0
  capturedStacks = []
  installed = false
}

/**
 * Re-invalidación moderada: como mucho UN RAF en vuelo y UNA revalidación por
 * ventana de MIN_REVALIDATION_MS. Las capturas durante el vuelo o dentro de la
 * ventana se cuentan pero no encolan otra revalidación — la que hay basta para
 * que el chart revalide las series en el frame siguiente.
 */
function scheduleRevalidation(getChart) {
  if (revalidationPending) return
  const now = Date.now()
  if (now - lastRevalidationAt < MIN_REVALIDATION_MS) return
  revalidationPending = true
  // RAF resuelto en cada llamada (no en carga del módulo) para respetar stubs
  // de test; con fallback a macrotarea en entornos sin RAF.
  const schedule =
    typeof requestAnimationFrame === 'function'
      ? requestAnimationFrame
      : (cb) => setTimeout(cb, 0)
  schedule(() => {
    revalidationPending = false
    lastRevalidationAt = Date.now()
    revalidationCount += 1
    try {
      const chart = typeof getChart === 'function' ? getChart() : null
      // Re-invalidación suave: fuerza un ciclo de update sin tocar opciones.
      chart?.applyOptions?.({})
    } catch { /* la contención nunca lanza */ }
  })
}

/**
 * Instala el handler global (idempotente). `getChart` es opcional: si se
 * proporciona, tras una captura se programa UNA re-invalidación suave del
 * chart por ráfaga para acelerar la recuperación del frame malo (las series
 * se revalidan solas al frame siguiente; el frenado evita el bucle
 * guardia→applyOptions→throw que produjo la tormenta de 465).
 */
export function installLwcCrashGuard(getChart) {
  if (installed || typeof window === 'undefined') return
  installed = true

  handlerRef = (event) => {
    if (!isLwcCompactionCrash(event)) return // cualquier otro error pasa intacto
    crashCount += 1
    // Solo para la firma exacta: descartamos el frame malo y logueamos gateado.
    event.preventDefault()

    // Ráfagas: capturas separadas por > BURST_GAP_MS pertenecen a ráfagas
    // distintas; de cada una se registran los primeros STACKS_PER_BURST stacks.
    const now = Date.now()
    if (now - lastCaptureAt > BURST_GAP_MS) {
      burstIndex += 1
      burstCaptureCount = 0
    }
    lastCaptureAt = now
    burstCaptureCount += 1

    if (burstCaptureCount <= STACKS_PER_BURST) {
      const stack = event.error?.stack || '(sin stack)'
      capturedStacks.push({ burst: burstIndex, occurrence: crashCount, stack })
      if (capturedStacks.length > MAX_STACKS_KEPT) {
        capturedStacks = capturedStacks.slice(-MAX_STACKS_KEPT)
      }
      debugWarn(`[lwcCrashGuard] Frame LWC descartado (bug #2044, ocurrencia #${crashCount}, ráfaga ${burstIndex}). Stack:\n${stack}`)
    } else {
      debugWarn(`[lwcCrashGuard] Frame LWC descartado (bug #2044, ocurrencia #${crashCount}, ráfaga ${burstIndex} ya con ${burstCaptureCount} capturas; stacks agotados para esta ráfaga).`)
    }

    scheduleRevalidation(getChart)

    // Contador visible en modo debug (?debug=1 o quantum_debug).
    window.__lwcCrashGuard = {
      crashCount,
      revalidationCount,
      burst: burstIndex,
      burstCaptureCount,
      stacks: capturedStacks.slice(),
    }
  }
  window.addEventListener('error', handlerRef, true)
}
